import { fileTypeLabel, formatFileSize } from "@/lib/ui/format";

export const reindexCopy = {
  label: "Обновить все индексы",
  pending: "Ставим в очередь…",
  queued: "Индекс обновляется. Документ снова появится в поиске и чате.",
  already: "Обновление индекса уже выполняется.",
  none: "Выберите документы",
  selectAll: "Все",
  stop: "Остановить",
  stopPending: "Останавливаем…",
  errorTitle: "Индекс",
} as const;

export const ocrCopy = {
  label: "Распознать",
  pending: "Ставим в очередь…",
  queued:
    "Распознавание поставлено в очередь. Текст обновится после обработки.",
  already: "Обработка документа уже выполняется.",
  errorTitle: "Распознавание",
  notApplicable: "—",
  statusNa: "Не применимо",
  ready: "OCR готов",
  busy: "OCR выполняется",
  alert: "Ошибка OCR",
} as const;

export const indexStageCopy = {
  tableLabel: "Стадии индексации",
  colStage: "Стадия",
  colStatus: "Статус",
  colWhen: "Когда",
  colDuration: "Время",
  colAction: "Действие",
  text: "Текст",
  textOcr: "Текст / OCR",
  vectors: "Поиск",
  facts: "Wiki",
  runText: "Разобрать",
  runOcr: "Распознать",
  runVectors: "Обновить",
  runFacts: "Извлечь",
  emptyWhen: "—",
  emptyDuration: "—",
  stageQueued: "Стадия поставлена в очередь.",
  vectorsNeedText: "Сначала нужен разобранный текст",
  factsNeedText: "Сначала нужен разобранный текст",
} as const;

export const DOCUMENT_TITLE_MAX = 80;

export const documentOverviewCopy = {
  title: "Название",
  titleHelp: "Так документ виден в поиске и чате. Имя файла не меняется.",
  titleSave: "Сохранить",
  titleSaving: "Сохраняем…",
  titleSaved: "Сохранено",
  invalidTitle: "Укажите название",
  titleTooLong: "Название не длиннее 80 символов.",
  file: "Файл",
  updated: "Обновлён",
  owner: "Владелец",
  status: "Статус",
  indexTitle: "Индексация",
  detailsTitle: "О документе",
  indexModel: "Модель",
  indexWhen: "Проиндексирован",
  indexCost: "Стоимость",
  indexEta: "Время",
  indexEmptyWhen: "Ещё не готово",
  indexEmptyCost: "Нет оценки",
  indexEmptyModel: "Не задана",
} as const;

export const documentTabsCopy = {
  overview: "Обзор",
  content: "Текст",
  analysis: "AI-анализ",
  versions: "Версии",
  access: "Доступ",
} as const;

export function documentSelectedTabIndex(params: {
  tab?: string;
  hasFragment: boolean;
  showAccess: boolean;
}): number {
  if (params.hasFragment || params.tab === "content") {
    return 1;
  }
  if (params.tab === "analysis") {
    return 2;
  }
  if (params.tab === "versions") {
    return 3;
  }
  if (params.tab === "access" && params.showAccess) {
    return 4;
  }
  return 0;
}

export const documentVersionCopy = {
  upload: "Загрузить версию",
  uploadHide: "Скрыть",
  submit: "Загрузить",
  pending: "Загружаем…",
  errorTitle: "Новая версия",
  stepFile: "Новый файл",
  listTitle: "Загруженные",
  current: "текущая",
  columnVersion: "Версия",
  columnFile: "Файл",
  columnSize: "Размер",
  columnStatus: "Статус",
  columnValidity: "Период",
  emptyTitle: "Версий нет",
  emptyDescription: "Загрузите файл, чтобы появилась первая версия.",
  validityEdit: "Период",
  validitySave: "Сохранить",
  validitySaving: "Сохраняем…",
  validityCancel: "Отмена",
  validityErrorTitle: "Период версии",
  validitySavedTitle: "Период",
  validitySaved: "Сохранено",
  validityFrom: "Действует с",
  validityTo: "Действует до",
  validityFromHelp: "Пусто — без нижней границы. День в UTC.",
  validityToHelp:
    "Пусто — без верхней границы. Интервал [с, до): день «до» не входит.",
  validityUnbound: "без срока",
  validityFromOnly: (from: string) => `с ${from}`,
  validityToOnly: (to: string) => `до ${to}`,
  validityRangeLabel: (from: string, to: string) => `${from} — ${to}`,
  validityInvalid: "Укажите дату в формате ГГГГ-ММ-ДД или оставьте пустым.",
  validityRange: "Дата «с» должна быть раньше даты «до».",
  validityDenied: "Нет права менять период версии",
  validityMissing: "Версия не найдена",
} as const;

export function documentVersionLabel(version: number | null): string {
  return version != null ? String(version) : "—";
}

export const fileDropCopy = {
  hint: "PDF, DOCX, TXT, MD · до 30 МБ",
  pick: "Выбрать файл",
} as const;

export const documentOverviewHint = {
  processing: "Документ обрабатывается. Скоро он появится в поиске и чате.",
  failed:
    "Обработка не удалась. Загрузите новую версию или обратитесь к администратору.",
  ready: "Документ готов. Его можно искать и задавать по нему вопросы.",
} as const;

export const documentContentEmpty = {
  pending: {
    title: "Текст ещё готовится",
    description: "Обычно это занимает несколько минут.",
  },
  failed: {
    title: "Не удалось извлечь текст",
    description: "Загрузите файл ещё раз или обратитесь к администратору.",
  },
  missing: {
    title: "Текста нет",
    description: "В этом файле нет текста для просмотра.",
  },
} as const;

export const askDocumentLabel = "Спросить AI";

export const documentPdfCopy = {
  title: "Оригинал PDF",
} as const;

export const documentContentViewCopy = {
  ariaLabel: "Режим просмотра текста",
  text: "Текст",
  original: "Оригинал",
  compare: "Сравнение",
  pageLabel: "Страница",
  pageOf: (page: number, total: number) => `${page} из ${total}`,
  prevPage: "Назад",
  nextPage: "Вперёд",
  emptyPageTitle: "На этой странице текста нет",
  emptyPageDescription: "Извлечение не дало текста для выбранной страницы.",
} as const;

export function askDocumentHref(documentId: string): string {
  return `/chat?documentId=${documentId}`;
}

export function documentFileHref(documentId: string): string {
  return `/documents/${documentId}/file`;
}

export function documentTabHref(documentId: string, tab: string): string {
  return `/documents/${documentId}?tab=${encodeURIComponent(tab)}`;
}

export const DOCUMENT_ANALYSIS_PAGE_SIZE = 10;
export const DOCUMENT_ANALYSIS_PAGE_SIZES = [10, 20, 50] as const;

export type DocumentAnalysisPageSize =
  (typeof DOCUMENT_ANALYSIS_PAGE_SIZES)[number];

export function parseDocumentAnalysisPageSize(
  raw: string | undefined,
): DocumentAnalysisPageSize {
  const n = Number.parseInt(raw ?? "", 10);
  return (DOCUMENT_ANALYSIS_PAGE_SIZES as readonly number[]).includes(n)
    ? (n as DocumentAnalysisPageSize)
    : DOCUMENT_ANALYSIS_PAGE_SIZE;
}

export function documentAnalysisHref(
  documentId: string,
  page = 1,
  pageSize: DocumentAnalysisPageSize = DOCUMENT_ANALYSIS_PAGE_SIZE,
): string {
  const params = new URLSearchParams();
  params.set("tab", "analysis");
  if (page > 1) {
    params.set("page", String(page));
  }
  if (pageSize !== DOCUMENT_ANALYSIS_PAGE_SIZE) {
    params.set("pageSize", String(pageSize));
  }
  return `/documents/${documentId}?${params.toString()}`;
}

export function documentSectionHref(
  documentId: string,
  section?: string | null,
): string {
  const base = `/documents/${documentId}`;
  const value = section?.trim();
  if (!value) {
    return base;
  }
  return `${base}?section=${encodeURIComponent(value)}`;
}

export function searchHitHref(
  hit: {
    documentId: string;
    chunkId: string;
  },
  query?: string,
): string {
  const params = new URLSearchParams({ chunk: hit.chunkId });
  const q = query?.trim();
  if (q) {
    params.set("q", q);
  }
  return `/documents/${hit.documentId}?${params.toString()}#document-section`;
}

/** Short needle for document highlight from a citation excerpt. */
export function citationHighlightQuery(excerpt?: string | null): string | undefined {
  const text = excerpt?.replace(/\s+/g, " ").trim();
  if (!text || text.length < 3) {
    return undefined;
  }
  const words = text.split(" ").filter(Boolean).slice(0, 10);
  const slice = words.join(" ");
  return slice.length > 72 ? slice.slice(0, 72).trim() : slice;
}

/** Open the cited document at the chunk when known; else fall back to section. */
export function citationDocumentHref(citation: {
  documentId: string;
  chunkId?: string;
  heading?: string | null;
  sectionPath?: string;
  excerpt?: string;
  documentExcerpt?: string;
}): string {
  if (citation.chunkId) {
    return searchHitHref(
      { documentId: citation.documentId, chunkId: citation.chunkId },
      citationHighlightQuery(citation.documentExcerpt || citation.excerpt),
    );
  }
  return documentSectionHref(
    citation.documentId,
    citation.heading || citation.sectionPath,
  );
}

type LocatedText = { before: string; match: string; after: string };

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function locateExact(text: string, needle: string): LocatedText | null {
  const index = text.toLowerCase().indexOf(needle.toLowerCase());
  if (index < 0) {
    return null;
  }
  return {
    before: text.slice(0, index),
    match: text.slice(index, index + needle.length),
    after: text.slice(index + needle.length),
  };
}

/** Cap pattern size: long chunks otherwise blow RegExp construction. */
const LOCATE_WORDS_MAX = 16;

function locateWords(text: string, words: string[]): LocatedText | null {
  if (words.length === 0) {
    return null;
  }
  let match: RegExpMatchArray | null;
  try {
    match = text.match(
      new RegExp(words.map(escapeRegExp).join("\\s+"), "iu"),
    );
  } catch {
    return null;
  }
  if (!match || match.index == null) {
    return null;
  }
  return {
    before: text.slice(0, match.index),
    match: match[0],
    after: text.slice(match.index + match[0].length),
  };
}

export function locateSection(
  text: string,
  section: string,
): LocatedText | null {
  const needle = section.trim();
  if (!text || !needle) {
    return null;
  }

  const exact = locateExact(text, needle);
  if (exact) {
    return exact;
  }

  const last = needle.split(" / ").at(-1)?.trim() ?? "";
  if (last && last.toLowerCase() !== needle.toLowerCase()) {
    return locateSection(text, last);
  }

  return null;
}

/** Find a search/chat fragment when chunk text does not copy extracted text 1:1. */
export function locateFragment(
  text: string,
  fragment: string,
): LocatedText | null {
  const needle = fragment.trim();
  if (!text || !needle) {
    return null;
  }

  const exact = locateExact(text, needle);
  if (exact) {
    return exact;
  }

  const words = needle
    .split(/\s+/)
    .filter((word) => word.length > 0)
    .slice(0, LOCATE_WORDS_MAX);
  if (words.length === 0) {
    return null;
  }
  const min = words.length <= 8 ? words.length : 6;
  for (let n = words.length; n >= min; n -= 1) {
    const located = locateWords(text, words.slice(0, n));
    if (located) {
      return located;
    }
  }

  if (words.length <= 8 || needle.includes(" / ")) {
    return locateSection(text, needle);
  }

  return null;
}

export function locateChunkInDocument(
  text: string,
  fragment: string,
  context?: string | null,
): LocatedText | null {
  const needle = fragment.trim();
  const scope = context?.trim();
  if (scope && scope !== needle) {
    const section = locateFragment(text, scope);
    if (section) {
      const inner = locateFragment(section.match, needle);
      if (inner) {
        return {
          before: section.before + inner.before,
          match: inner.match,
          after: inner.after + section.after,
        };
      }
      return section;
    }
  }
  return locateFragment(text, needle);
}

export function documentHeaderMeta(input: {
  version: number | null;
  mimeType: string | null;
  originalName?: string;
  ingestSource?: string | null;
  sizeBytes?: number | null;
}): string {
  const parts = [
    input.version != null ? `Версия ${input.version}` : null,
    fileTypeLabel(input.mimeType, input.originalName, input.ingestSource),
    input.sizeBytes != null ? formatFileSize(input.sizeBytes) : null,
  ];
  return parts.filter((part): part is string => Boolean(part)).join(" · ");
}

export function documentOverviewMessage(status: string): string {
  if (status === "READY") return documentOverviewHint.ready;
  if (status === "FAILED") return documentOverviewHint.failed;
  return documentOverviewHint.processing;
}

export function parseDocumentTitle(
  raw: string,
): { ok: true; value: string } | { ok: false; error: string } {
  const title = raw.trim().replace(/\s+/g, " ");
  if (!title) {
    return { ok: false, error: documentOverviewCopy.invalidTitle };
  }
  if (title.length > DOCUMENT_TITLE_MAX) {
    return { ok: false, error: documentOverviewCopy.titleTooLong };
  }
  return { ok: true, value: title };
}

export function documentContentEmptyState(
  parseStatus: string,
  extractedText: string | null,
): { title: string; description: string } | null {
  if (extractedText?.trim()) {
    return null;
  }
  if (parseStatus === "FAILED") {
    return documentContentEmpty.failed;
  }
  if (parseStatus === "DONE") {
    return documentContentEmpty.missing;
  }
  return documentContentEmpty.pending;
}

/** Plain-text needles for DOM highlight after react-markdown splits nodes. */
export function stripMarkdownForDomSearch(value: string): string {
  return value
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/_([^_]+)_/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\\([.\\[\]()])/g, "$1");
}

export function buildDomHighlightNeedles(
  ...sources: Array<string | null | undefined>
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (raw: string) => {
    const plain = stripMarkdownForDomSearch(raw).trim();
    if (!plain) {
      return;
    }
    const firstLine = plain.split("\n")[0]?.trim() ?? plain;
    const variants = [firstLine];
    if (firstLine.length > 64) {
      variants.push(firstLine.slice(0, 64).trim());
    }
    const words = firstLine.split(/\s+/).filter((word) => word.length > 0);
    for (const n of [8, 5, 3, 2]) {
      if (words.length >= n) {
        variants.push(words.slice(0, n).join(" "));
      }
    }
    for (const variant of variants) {
      const key = variant.toLocaleLowerCase("ru-RU");
      if (variant.length < 2 || seen.has(key)) {
        continue;
      }
      seen.add(key);
      out.push(variant);
    }
  };
  for (const source of sources) {
    if (source) {
      push(source);
    }
  }
  return out;
}

/** Keep «1. / 1)» visible: GFM would make <ol>, Carbon sets list-style: none. */
export function escapeMarkdownListMarkers(text: string): string {
  return text.replace(
    /^(\d+(?:\.\d+)*)([.)])(\s)/gm,
    (_full, id: string, mark: string, space: string) =>
      `${id}\\${mark}${space}`,
  );
}
